import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { History, RotateCcw, Trash2, Save, Loader2, ShieldCheck, Info, Sparkles } from "lucide-react";

const COLL_LABEL = { settings: "Ayarlar", sectors: "Sektörler", price_rules: "Fiyat Kuralları", role_permissions: "Rol Yetkileri" };

export default function PivotPoints() {
  const [list, setList] = useState([]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/ai/pivot/list"); setList(r.data || []); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try { await api.post("/ai/pivot/create", { label: label.trim() }); toast.success("Pivot noktası (yedek) alındı"); setLabel(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const restore = async (p) => {
    if (!window.confirm(`"${p.label}" anına GERİ DÖNÜLECEK.\n\nMevcut Ayarlar, Sektörler, Fiyat Kuralları ve Rol Yetkileri bu yedekle DEĞİŞTİRİLECEK. (Satış/stok gibi işlemsel veriler etkilenmez.)\n\nGüvenlik için mevcut durumun otomatik yedeği alınır. Devam edilsin mi?`)) return;
    setBusy(true);
    try { await api.post(`/ai/pivot/${p.id}/restore`); toast.success("Sistem bu pivota geri döndürüldü. Değişikliklerin görünmesi için sayfayı yenileyin."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`"${p.label}" yedeği silinsin mi?`)) return;
    try { await api.delete(`/ai/pivot/${p.id}`); toast.success("Yedek silindi"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("tr-TR"); } catch { return iso; } };

  return (
    <div data-testid="pivot-points" className="space-y-4">
      <div>
        <p className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><History className="h-5 w-5 text-brand-green" /> Pivot Noktası — Yedek / Geri Al</p>
        <p className="mt-1 text-sm text-slate-500">Yapılandırma katmanının anlık fotoğrafını alır: <b>Ayarlar, Sektörler, Fiyat Kuralları, Rol Yetkileri</b>. AI’ın önerisiyle veya elle yaptığınız bir değişiklik işe yaramazsa tek tıkla o ana geri dönün. Satış/stok gibi işlemsel veriler etkilenmez.</p>
      </div>

      <div data-testid="pivot-info" className="flex items-start gap-2 rounded-xl border border-brand-green/20 bg-brand-green/[0.04] p-3 text-[11px] leading-snug text-slate-600">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-green" />
        <span>Riskli bir değişiklikten (toplu fiyat, sektör/modül düzenlemesi, rol değişikliği) ÖNCE bir pivot alın. Geri alırken mevcut durumun otomatik yedeği de saklanır — yani geri almayı da geri alabilirsiniz.</span>
      </div>

      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 bg-white p-4">
        <div className="min-w-0 flex-1">
          <label className="mb-1 block text-[11px] font-semibold text-slate-500">Yedek adı (opsiyonel)</label>
          <Input data-testid="pivot-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Örn: Zamlı fiyatlardan önce" />
        </div>
        <Button data-testid="pivot-create-btn" onClick={create} disabled={busy} className="gap-1.5">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Pivot Al
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Yükleniyor…</p>
      ) : list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-8 text-center">
          <Sparkles className="mx-auto mb-2 h-6 w-6 text-slate-300" />
          <p className="text-sm text-slate-400">Henüz pivot yok. Önemli bir değişiklikten önce ilk yedeğinizi alın.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((p) => (
            <div key={p.id} data-testid={`pivot-row-${p.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${p.auto ? "bg-amber-100 text-amber-600" : "bg-brand-green/10 text-brand-green"}`}>
                {p.auto ? <RotateCcw className="h-4 w-4" /> : <History className="h-4 w-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-slate-800">{p.label} {p.auto && <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700">oto</span>}</p>
                <p className="text-[11px] text-slate-400">{fmt(p.created_at)} · {p.created_by} · {Object.entries(p.counts || {}).map(([k, v]) => `${COLL_LABEL[k] || k}: ${v}`).join(" · ")}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <Button data-testid={`pivot-restore-${p.id}`} onClick={() => restore(p)} disabled={busy} size="sm" variant="outline" className="gap-1.5 border-brand-green/40 text-brand-green hover:bg-brand-green/5"><RotateCcw className="h-3.5 w-3.5" /> Geri Al</Button>
                <button data-testid={`pivot-delete-${p.id}`} onClick={() => del(p)} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
